<template>
    <div>
        <b-row class="mb-3">
            <b-col><span class="h3">Coin Pricing</span></b-col>
            <b-col>
                <b-button class="float-right" variant="success"
                            :disabled="isLoading || isSaving"
                            @click="startCreating()">
                    <i class="fa-solid fa-plus"></i> Add New
                </b-button>
            </b-col>
        </b-row>
        <b-card>
            <b-row class="mb-2">
                <b-col>

                        <b-input-group size="sm">
                            <b-form-input
                            id="filter-input"
                            v-model="filter"
                            type="search"
                            placeholder="Type to Search"
                            ></b-form-input>

                            <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                            </b-input-group-append>
                        </b-input-group>
                  
                </b-col>
            </b-row>
            
            <!--view table-->
            <b-table small
                     bordered
                     striped
                     head-variant="light"
                     class="text-center mb-0 font-xs"
                     thead-class="font-xs"
                     :busy="isLoading"
                     :items="coin_definitions"
                     :fields="coin_definitions_fields"
                     :sort-by.sync="sortBy"
                     :sort-desc.sync="sortDesc"
                     :filter="filter">
                <template v-slot:table-busy>
                    <LoadingSpinner :busy="isLoading"></LoadingSpinner>
                </template>
                <template v-slot:cell(item_style_id)="data">
                    <div class="font-xxs">Style ID: {{ data.value }}</div>
                    <div class="font-xxs">Class ID: {{ data.item.item_class_id }}</div>
                </template>
                <template v-slot:cell(metal)="data">
                    {{ data.value | titleCase }}
                </template>
                <template v-slot:cell(purity_percent)="data">
                    {{ data.value | toFixed2orNull }}
                </template>
                <template v-slot:cell(purity_karat)="data">
                    {{ data.value | toFixed2orNull }}
                </template>
                <template v-slot:cell(auto_set_to_hold)="data">
                    {{ data.value ? '✅' : '❌' }}
                </template>
                <template v-slot:cell(image_url)="data">
                    <img :src="data.value" style="width:120px;"/>
                </template>
                <template v-slot:cell(edit)="data">
                    <b-button class="ml-2 mb-2" size="sm"
                              :disabled="isLoading || isSaving"
                              @click="startEdits(data.item)"
                              variant="info"><i class="fa-solid fa-edit"></i>
                    </b-button>
                    <i class="fa-thin fa-gear" style="cursor: help;"
                        v-b-tooltip.hover.right="'s_id: ' + data.item.item_style_id + ' | c_id: ' + data.item.item_class_id"></i>
                </template>
            </b-table>

        </b-card>

<!-- EDIT COIN MODAL -->
        <b-modal id="modal-coin-edit" :title="isCreating ? 'Add Coin' : 'Edit Coin'" size="lg" centered
                 hide-header-close
                 no-close-on-backdrop
                 no-close-on-esc
                 ok-variant="success"
                 :ok-disabled="isSaving"
                 :cancel-disabled="isSaving"
                 @cancel="cancelEdits"
                 @ok="saveEdits">
            <template #modal-ok="{ ok }">
                <i v-if="!isSaving" class="ml-1 fa-regular fa-save fa-fw"></i>
                <i v-else class="fa-regular fa-spin fa-spinner fa-fw"></i>
                Save
            </template>

            <b-form-row>
                <b-col>
                    <b-form-group
                        label="Id:"
                        label-for="input-id"
                    >
                        <b-form-input
                            id="input-id"
                            v-model="itemForEdit.id"
                            type="text"
                            disabled
                            plaintext
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Item Style Id:"
                        label-for="input-item-style-id"
                    >
                        <b-form-input
                            id="input-item-style-id"
                            v-model="itemForEdit.item_style_id"
                            type="text"
                            number
                            disabled
                            plaintext
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Item Class Id:"
                        label-for="input-item-class-id"
                    >
                        <b-form-input
                            id="input-item-class-id"
                            v-model="itemForEdit.item_class_id"
                            type="text"
                            number
                            disabled
                            plaintext
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Metal:"
                        label-for="input-metal"
                    >
                        <b-form-select
                            id="input-metal"
                            v-model="itemForEdit.metal"
                            :options="coinDefinitionsMetalOptions"
                            :disabled="fieldsLocked"
                        ></b-form-select>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col>
                    <b-form-group
                        label="Region:"
                        label-for="input-region"
                    >
                        <b-form-select
                            id="input-select-region"
                            v-model="itemForEdit.item_type"
                            :options="itemTypeOptions"
                            :disabled="fieldsLocked"
                        ></b-form-select>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Coin Name:"
                        label-for="input-series"
                    >
                        <b-form-input
                            id="input-series"
                            v-model="itemForEdit.series"
                            type="text"
                            :readonly="fieldsLocked"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col>
                    <b-form-group
                        label="Troy Ounce:"
                        label-for="input-troy-ounce"
                        description="Total physical weight of coin, in Troy Oz."
                    >
                        <b-form-input
                            id="input-id"
                            v-model="itemForEdit.troy_ounce"
                            type="text"
                            number
                            :readonly="fieldsLocked"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Purity Percent:"
                        label-for="input-purity-percent"
                    >
                        <b-form-input
                            id="input-purity-percent"
                            v-model="itemForEdit.purity_percent"
                            type="number"
                            step="0.01"
                            :readonly="fieldsLocked"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Purity Karat:"
                        label-for="input-purity-karat"
                    >
                        <b-form-input
                            id="input-purity-karat"
                            v-model="itemForEdit.purity_karat"
                            type="number"
                            step="0.01"
                            :readonly="fieldsLocked"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col cols="2">
                    <b-form-group
                        label="Purity Type:"
                        label-for="input-purity-type"
                    >
                        <b-form-select
                            id="input-purity-type"
                            v-model="itemForEdit.purity_type"
                            :options="coinDefinitionsPurityOptions"
                            :disabled="fieldsLocked"
                        ></b-form-select>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col cols="4">
                    <b-form-group
                        label="Price (Premium):"
                        label-for="input-price"
                        description="Format: [+|-][$]<number>[%]"
                    >
                        <b-input-group>
                            <b-form-input
                                id="input-price"
                                v-model="itemForEdit.price"
                                type="text"
                                placeholder="Leave blank to use metal pricing."
                            ></b-form-input>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col cols="3">
                    <b-form-group
                        label="Stated Price:"
                        label-for="input-stated-price"
                        description="Fixed dollar amount from NCII."
                    >
                        <b-input-group prepend="$">
                            <b-form-input
                                id="input-stated-price"
                                v-model.number="itemForEdit.stated_price"
                                type="number"
                                step="0.01"
                            ></b-form-input>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col cols="3">
                    <b-form-group
                        label="Spot Adjustment:"
                        label-for="input-spot-adjustment"
                        description="Spot offset per Oz from NCII. (Overrides Premium)"
                    >
                        <b-input-group prepend="$">
                            <b-form-input
                                id="input-spot-adjustment"
                                v-model.number="itemForEdit.spot_adjustment_per_oz"
                                type="number"
                                step="0.01"
                            ></b-form-input>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group
                        label="Auto Set to Hold:"
                        label-for="input-auto-set-to-hold"
                    >
                        <b-form-checkbox v-model="itemForEdit.auto_set_to_hold"
                            name="input-auto-set-to-hold"
                            id="input-auto-set-to-hold"
                            switch
                            value="1"
                            size="lg"
                            unchecked-value="0">
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col>
                    <b-form-group
                        label="Image URL:"
                        label-for="input-image-url"
                    >
                        <b-form-input
                            id="input-1"
                            v-model="itemForEdit.image_url"
                            type="text"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-card>
                <b-img :src="itemForEdit.image_url" fluid center></b-img>
            </b-card>
        </b-modal>

    </div>
</template>

<script>
import _ from 'lodash';
import LoadingSpinner from '@/components/LoadingSpinner';

export default {
    name: 'coin-pricing-view',
    components: {
        LoadingSpinner,
    },
    data() {
        return {
            isLoading: false,
            isEditing: false,
            isSaving: false,
            isCreating: false,
            sortBy: 'metal',
            sortDesc: false,
            filter: null,
            coin_definitions: [],
            coin_definitions_fields: [
                { key: 'region', sortable: true },
                { key: 'metal', sortable: true },
                { key: 'series', label: 'Coin Name', sortable: true },
                { key: 'troy_ounce', sortable: true },
                { key: 'purity_percent', sortable: true },
                { key: 'purity_karat', sortable: true },
                { key: 'purity_type', sortable: true },
                { key: 'price', sortable: true },
                { key: 'stated_price', sortable: true },
                { key: 'spot_adjustment_per_oz', label: 'Spot Adjustment', sortable: true },
                { key: 'auto_set_to_hold', label: 'Auto-Hold', sortable: true },
                { key: 'image_url', label: 'Image' },
                'edit',
            ],
            coin_definitions_cache: [],
            coinDefinitionsMetalOptions: [
                {value: null, text: 'Select a Metal...', disabled: true},
                {value: 'gold', text: 'Gold'},
                {value: 'silver', text: 'Silver'},
                {value: 'platinum', text: 'Platinum'},
            ],
            coinDefinitionsPurityOptions: [
                {value: 'K', text: 'K'},
                {value: '%', text: '%'},
            ],
            itemForEditEmpty: {
                'id': null,
                'item_style_id': null,
                'item_class_id': null,
                'region': null,
                'metal': null,
                'series': null,
                'troy_ounce': null,
                'agw': null,
                'purity_percent': null,
                'purity_karat': null,
                'purity_type': '%',
                'price': null,
                'stated_price': null,
                'melt_price': null,
                'spot_adjustment_per_oz': null,
                'auto_set_to_hold': 1,
                'image_url': null,
                'item_style': {},
                'item_type': {}
            },
            itemForEdit: {
                item_style: {},
                item_type: {},
            },
            itemTypeOptions: [
                {text: 'Select a Region...', value: {}, disabled: true},
                {text: 'American Coin/Bullion', value: {id: 14, region: 'American'}},
                {text: 'Australian Coin/Bullion', value: {id: 15, region: 'Australian'}},
                {text: 'British Coin/Bullion', value: {id: 16, region: 'British'}},
                {text: 'Canadian Coin/Bullion', value: {id: 17, region: 'Canadian'}},
                {text: 'Chinese Coin/Bullion', value: {id: 18, region: 'Chinese'}},
                {text: 'European Coin/Bullion', value: {id: 19, region: 'European'}},
                {text: 'French Coin/Bullion', value: {id: 20, region: 'French'}},
                {text: 'German Coin/Bullion', value: {id: 21, region: 'German'}},
                {text: 'Mexican Coin/Bullion', value: {id: 22, region: 'Mexican'}},
                {text: 'Russian Coin/Bullion', value: {id: 23, region: 'Russian'}},
                {text: 'South African Coin/Bullion', value: {id: 24, region: 'South African'}},
                {text: 'Foreign Coin/Bullion', value: {id: 58, region: 'Foreign'}},
            ],
        };
    },
    created() {
        this.getAllData();
    },
    watch: {
        'itemForEdit.metal'(newValue) {
            switch (newValue) {
                case 'gold':
                    this.itemForEdit.item_class_id = 1;
                    break;
                case 'silver':
                    this.itemForEdit.item_class_id = 4;
                    break;
                case 'platinum':
                    this.itemForEdit.item_class_id = 6;
                    break;
                default:
                    this.itemForEdit.item_class_id = null;
                    break;
            }
        },
        'itemForEdit.item_type'(newValue) {
            if (newValue) {
                this.itemForEdit.region = newValue.region;
            }
        },
    },
    methods: {
        getAllData() {
            this.isLoading = true;
            this.$http.get('/coin-definitions').then(response => {
                this.coin_definitions = response.data.data;
                this.coin_definitions.forEach(coin_definition => {
                    if (coin_definition.item_style !== null) {
                        coin_definition.item_type = _.find(this.itemTypeOptions, ['value.id', coin_definition.item_style.item_type_id]).value;
                    } else {
                        coin_definition.item_type = {value: {id: null, region: null}};
                    }
                });
                this.coin_definitions_cache = _.cloneDeep(this.coin_definitions);
                this.isLoading = false;
            });
        },

        startCreating() {
            this.isEditing = true;
            this.isCreating = true;
            this.itemForEdit = _.cloneDeep(this.itemForEditEmpty);
            this.$bvModal.show('modal-coin-edit');
        },

        startEdits(item) {
            this.isEditing = true;
            this.itemForEdit = item;
            this.itemForEdit.item_type = _.find(this.itemTypeOptions, ['value.id', this.itemForEdit.item_style.item_type_id]).value;
            this.$bvModal.show('modal-coin-edit');
        },

        cancelEdits() {
            this.coin_definitions = _.cloneDeep(this.coin_definitions_cache);
            this.isEditing = false;
            this.isCreating = false;
            this.itemForEdit = _.cloneDeep(this.itemForEditEmpty);
            this.$bvModal.hide('modal-coin-edit');
        },

        saveEdits(bvModalEvent) {
            this.isSaving = true;
            bvModalEvent.preventDefault();
            this.itemForEdit.region = this.itemForEdit.item_type.region;

            let url = '/coin-definitions/' + this.itemForEdit.id;
            let method = 'put';
            if (this.isCreating) {
                url = '/coin-definitions/';
                method = 'post';
            }
            
            let config = {
                method: method,
                data: this.itemForEdit
            };

            this.$http.request(url, config)
                .then(response => {
                    this.coin_definitions = response.data.data;
                    this.coin_definitions_cache = _.cloneDeep(this.coin_definitions);
                    this.$nextTick(() => {
                        this.isSaving = false;
                        this.isEditing = false;
                        this.isCreating = false;
                        this.itemForEdit = _.cloneDeep(this.itemForEditEmpty);
                        this.$bvModal.hide('modal-coin-edit');
                    });
                });
        },

    },
    computed: {
        fieldsLocked() {
            return !_.find(this.$store.getters.getProfile.roles.data, {name: 'Super Admin'});
        }
    }
};
</script>

<style scoped>

</style>
